export type StageId =
  | 'text'
  | 'tokens'
  | 'embeddings'
  | 'meaning'
  | 'attention'
  | 'layers'
  | 'prediction'

export type HeadId = 'previous' | 'meaning' | 'first'

export type ClusterId =
  | 'animals'
  | 'surfaces'
  | 'places'
  | 'actions'
  | 'grammar'
  | 'qualities'
  | 'pieces'
  | 'punctuation'

export type Vec3 = [number, number, number]

export type StageDef = {
  id: StageId
  short: string
  title: string
  takeaway: string
  body: string
  hint: string
}

export const STAGES: StageDef[] = [
  {
    id: 'text',
    short: 'Text',
    title: 'It starts with text',
    takeaway: 'A language model has one job: guess what comes next.',
    body: "Give it the start of a sentence and it predicts the next small piece of text. Everything you're about to see happens again for every single piece it writes.",
    hint: 'Pick a different sentence from the menu in the control bar.',
  },
  {
    id: 'tokens',
    short: 'Tokens',
    title: 'Text becomes tokens',
    takeaway: 'The model reads tokens, not letters or words.',
    body: 'A tokenizer cuts text into chunks from a fixed vocabulary of roughly 50,000 to 200,000 pieces. Common words are one token; rarer words split into parts. To the model, each token is just an ID number.',
    hint: 'Try the “Tokenization…” example to watch one word split into three tokens.',
  },
  {
    id: 'embeddings',
    short: 'Embeddings',
    title: 'Each token becomes a list of numbers',
    takeaway:
      'Every token ID looks up its own vector: a list of numbers learned in training.',
    body: 'Real models use thousands of numbers per token, often 4,096 or more. We show 8. Blue is negative, amber is positive. Nobody chose these values by hand; training adjusted them until they were useful.',
    hint: 'Click a token to read its numbers.',
  },
  {
    id: 'meaning',
    short: 'Meaning',
    title: 'Numbers are a location',
    takeaway: 'Tokens with similar meanings end up close together.',
    body: "Treat a token's numbers as coordinates and every token gets a spot in a vast space. We've squashed thousands of dimensions down to 3 so you can see it. Animals cluster together; places sit far away.",
    hint: 'Orbit around the cloud. Click a token to highlight its neighbors.',
  },
  {
    id: 'attention',
    short: 'Attention',
    title: 'Tokens look back at each other',
    takeaway:
      'Attention lets each token pull in information from earlier tokens.',
    body: "Each token scores every earlier token for relevance, then blends in what it finds. It can only look left, never at words that haven't been written yet. Models run many attention heads at once, each tuned to a different pattern.",
    hint: 'Click any token to see what it looks at, then switch heads.',
  },
  {
    id: 'layers',
    short: 'Layers',
    title: 'Repeat, many times',
    takeaway:
      "Stacked layers refine every token's vector, one step at a time.",
    body: "One layer is attention (tokens share information) plus a feed-forward network (each vector is transformed on its own). Big models stack dozens of layers; GPT-3 has 96. By the top, the last token's vector encodes a guess about what comes next.",
    hint: 'Watch the flow, and orbit to see the stack from the side.',
  },
  {
    id: 'prediction',
    short: 'Predict',
    title: 'Scores become probabilities',
    takeaway:
      'The model ends with a probability for every token in its vocabulary.',
    body: "The last token's final vector is scored against every token in the vocabulary. Softmax turns those scores into probabilities that add up to 100%. Pick one, add it to the text, and run everything again. That loop is how it writes.",
    hint: 'Drag the temperature slider, then sample the next token. Keep going to write a whole phrase.',
  },
]

export type Preset = {
  id: string
  label: string
  /** The chat message this reply is answering. */
  userMessage: string
  tokens: [string, number][]
}

export const PRESETS: Preset[] = [
  {
    id: 'cat',
    label: 'The cat sat on the',
    userMessage: 'Tell me a short story about a cat.',
    tokens: [
      ['The', 464],
      [' cat', 3797],
      [' sat', 3332],
      [' on', 319],
      [' the', 262],
    ],
  },
  {
    id: 'france',
    label: 'The capital of France is',
    userMessage: "What's the capital of France?",
    tokens: [
      ['The', 464],
      [' capital', 3139],
      [' of', 286],
      [' France', 4881],
      [' is', 318],
    ],
  },
  {
    id: 'subword',
    label: 'Tokenization is unbelievably',
    userMessage: 'What do you think of tokenization?',
    tokens: [
      ['Token', 30642],
      ['ization', 1634],
      [' is', 318],
      [' un', 555],
      ['believ', 6667],
      ['ably', 1346],
    ],
  },
]

export const MAX_TOKENS = 10

export const TOKEN_COLORS = [
  '#A78BFA',
  '#22D3EE',
  '#FBBF24',
  '#34D399',
  '#F472B6',
  '#60A5FA',
  '#FB923C',
  '#A3E635',
]

export type Cluster = {
  id: ClusterId
  label: string
  center: Vec3
  words: string[]
}

// Animals sit near Actions and Surfaces, Places sit far from all three, and
// Grammar stays central because function words appear alongside everything.
export const CLUSTERS: Cluster[] = [
  {
    id: 'animals',
    label: 'Animals',
    center: [-4.6, 1.1, -0.5],
    words: ['cat', 'dog', 'bird', 'horse', 'mouse', 'fox'],
  },
  {
    id: 'surfaces',
    label: 'Surfaces',
    center: [-1.9, 3.0, -3.1],
    words: ['mat', 'floor', 'couch', 'bed', 'chair', 'rug', 'windowsill', 'roof'],
  },
  {
    id: 'actions',
    label: 'Actions',
    center: [-3.1, -1.6, 2.4],
    words: ['sat', 'ran', 'slept', 'jumped', 'stood', 'purring'],
  },
  {
    id: 'grammar',
    label: 'Grammar',
    center: [0.7, 0.0, 0.4],
    words: ['the', 'a', 'of', 'on', 'is', 'to', 'and'],
  },
  {
    id: 'places',
    label: 'Places',
    center: [6.0, 1.4, -1.9],
    words: ['France', 'Paris', 'Italy', 'Rome', 'Japan', 'Tokyo', 'capital'],
  },
  {
    id: 'qualities',
    label: 'Qualities',
    center: [3.6, -1.7, 2.9],
    words: ['useful', 'important', 'tricky', 'fun', 'simple', 'weird'],
  },
  {
    id: 'pieces',
    label: 'Word pieces',
    center: [2.8, 3.4, -2.6],
    words: ['Token', 'ization', 'un', 'believ', 'ably', 'word', 'text'],
  },
  {
    id: 'punctuation',
    label: 'Punctuation',
    center: [-0.2, -2.6, -2.9],
    words: ['.', ',', '!'],
  },
]

/** Where generated tokens that aren't background words belong. */
export const EXTRA_CLUSTER: Record<string, ClusterId> = {
  it: 'grammar',
  this: 'grammar',
  but: 'grammar',
  so: 'grammar',
  for: 'grammar',
  when: 'grammar',
  which: 'grammar',
  while: 'grammar',
  all: 'grammar',
  not: 'grammar',
  by: 'grammar',
  next: 'grammar',
  beside: 'grammar',
  known: 'actions',
  located: 'actions',
  unbelievably: 'qualities',
  '(': 'punctuation',
}

export const FUNCTION_CLUSTERS: ClusterId[] = ['grammar', 'punctuation']

export const HEADS: { id: HeadId; label: string; description: string }[] = [
  {
    id: 'previous',
    label: 'Previous word',
    description: 'Looks at the token right before this one.',
  },
  {
    id: 'meaning',
    label: 'Meaning',
    description:
      'Looks for earlier tokens related in meaning, favoring content words over grammar words.',
  },
  {
    id: 'first',
    label: 'First token',
    description:
      'Parks attention on the first token when nothing else stands out.',
  },
]

type NextTokenRule = {
  match: (sentence: string) => boolean
  candidates: [string, number][]
}

const SURFACES = ['floor', 'couch', 'bed', 'windowsill', 'roof']
const QUALITIES = ['useful', 'important', 'tricky', 'fun', 'simple', 'weird']

/** First match wins, so the catch-all rules sit at the end. */
export const NEXT_TOKEN_RULES: NextTokenRule[] = [
  {
    match: (s) => s.endsWith('sat on the'),
    candidates: [
      [' mat', 3.1],
      [' floor', 2.4],
      [' couch', 2.1],
      [' bed', 1.8],
      [' windowsill', 1.4],
      [' roof', 0.9],
    ],
  },
  {
    match: (s) => s.endsWith('sat on the mat'),
    candidates: [
      ['.', 3.0],
      [',', 2.1],
      [' and', 2.0],
      [' purring', 1.1],
      [' all', 0.8],
      [' while', 0.7],
    ],
  },
  {
    match: (s) => SURFACES.some((w) => s.endsWith(`sat on the ${w}`)),
    candidates: [
      ['.', 2.8],
      [' and', 2.1],
      [',', 1.9],
      [' next', 1.3],
      [' by', 1.0],
      [' beside', 0.8],
    ],
  },
  {
    match: (s) => s.endsWith('capital of France is'),
    candidates: [
      [' Paris', 4.6],
      [' a', 1.7],
      [' the', 1.4],
      [' known', 1.0],
      [' located', 0.8],
      [' not', 0.2],
    ],
  },
  {
    match: (s) => s.endsWith('France is Paris'),
    candidates: [
      ['.', 3.3],
      [',', 2.4],
      [' and', 1.6],
      [' which', 0.9],
      [' (', 0.8],
      ['!', 0.1],
    ],
  },
  {
    match: (s) => s.endsWith('is unbelievably'),
    candidates: [
      [' useful', 2.6],
      [' important', 2.3],
      [' tricky', 2.1],
      [' fun', 1.5],
      [' simple', 1.0],
      [' weird', 0.9],
    ],
  },
  {
    match: (s) => QUALITIES.some((w) => s.endsWith(`unbelievably ${w}`)),
    candidates: [
      ['.', 2.9],
      [' for', 2.3],
      [' when', 1.5],
      [',', 1.4],
      [' and', 1.2],
      ['!', 0.6],
    ],
  },
  {
    match: (s) => /[.!]$/.test(s),
    candidates: [
      [' It', 2.3],
      [' The', 2.1],
      [' This', 1.7],
      [' But', 1.3],
      [' And', 1.0],
      [' So', 0.8],
    ],
  },
  {
    match: () => true,
    candidates: [
      ['.', 2.4],
      [',', 2.0],
      [' and', 1.8],
      [' the', 1.1],
      [' to', 1.0],
      [' of', 0.8],
    ],
  },
]

export type GlossaryKey =
  | 'token'
  | 'tokenId'
  | 'vector'
  | 'attention'
  | 'head'
  | 'layer'
  | 'logit'
  | 'softmax'
  | 'temperature'

export const GLOSSARY: Record<GlossaryKey, { term: string; def: string }> = {
  token: {
    term: 'Token',
    def: "A chunk of text from the model's fixed vocabulary, like a word or part of a word.",
  },
  tokenId: {
    term: 'Token ID',
    def: 'The number that stands for a token in the vocabulary.',
  },
  vector: {
    term: 'Vector / embedding',
    def: 'The list of numbers a token ID looks up, learned during training.',
  },
  attention: {
    term: 'Attention',
    def: 'How a token scores earlier tokens for relevance and blends in their information.',
  },
  head: {
    term: 'Head',
    def: 'One independent attention pattern. Models run many in parallel.',
  },
  layer: {
    term: 'Layer',
    def: 'One round of attention plus a feed-forward network. Models stack many.',
  },
  logit: {
    term: 'Logit',
    def: 'The raw score the model gives each candidate token.',
  },
  softmax: {
    term: 'Softmax',
    def: 'The step that turns scores into probabilities that add up to 100%.',
  },
  temperature: {
    term: 'Temperature',
    def: 'A setting that sharpens or flattens the probabilities before a token is picked.',
  },
}

export const SIMPLIFICATIONS = [
  '8 numbers per token instead of thousands.',
  '3 dimensions of meaning space instead of thousands.',
  '6 layers instead of dozens.',
  '3 hand-made attention heads instead of many learned ones.',
  '6 candidates instead of the whole vocabulary.',
  'Only the reply so far; a real model also reads your message and the rest of the conversation.',
  'Every number is illustrative, not from a real model.',
]

export const TEMPERATURE_COPY =
  'Temperature reshapes the odds. Low temperature makes the top choice dominate; high temperature flattens the bars so unlikely tokens get picked more often.'
